import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiPackage, FiTarget } = FiIcons;

const ProductInfo = () => {
  const layers = [
    { id: 1, name: 'Ochranná vrstva', desc: 'Povrchová ochrana proti skvrnám', colorClass: 'bg-accent-teal text-white border-accent-teal' },
    { id: 2, name: 'Dekorativní design', desc: 'Střední tisková vrstva HD', colorClass: 'bg-accent-sage text-white border-accent-sage' },
    { id: 3, name: 'Základní tisk', desc: 'Spodní tisková vrstva', colorClass: 'bg-arch-light dark:bg-dark-surface text-arch-gray dark:text-gray-400' },
    { id: 4, name: 'PUR Pojivo', desc: 'Mikrogranulární vrstva', colorClass: 'bg-arch-light dark:bg-dark-surface text-arch-gray dark:text-gray-400' },
    { id: 5, name: 'Ochranná bariéra', desc: '1mm krystalická koextrudovaná vrstva', colorClass: 'bg-accent-clay text-white border-accent-clay' },
    { id: 6, name: 'Bambusové jádro', desc: '3mm / 6mm ekologické uhlíkové jádro', colorClass: 'bg-brand-500 text-white border-brand-500' },
    { id: 7, name: 'Spodní bariéra', desc: '1mm krystalická koextrudovaná vrstva', colorClass: 'bg-accent-clay text-white border-accent-clay' }
  ];

  return (
    <section id="product" className="py-32 bg-white dark:bg-dark-bg relative border-b border-arch-border dark:border-dark-border transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <motion.div className="mb-20" initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }}>
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-8 h-[1px] bg-brand-500"></div>
            <span className="text-xs font-bold text-arch-gray dark:text-gray-400 uppercase tracking-widest">Detail produktu</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-arch-dark dark:text-white mb-6 tracking-tight">Struktura panelu.</h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start mb-24">
          
          {/* Layer List - Colorful */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h3 className="text-xl font-bold text-arch-dark dark:text-white mb-8 flex items-center uppercase tracking-widest">
              <SafeIcon icon={FiLayers} className="w-5 h-5 mr-4 text-accent-teal" />
              Složení vrstev
            </h3>
            
            <div className="space-y-3">
              {layers.map((layer) => (
                <div key={layer.id} className="flex border border-arch-border dark:border-dark-border bg-white dark:bg-dark-bg group hover:border-brand-500 dark:hover:border-brand-500 transition-colors">
                  <div className={`w-12 flex items-center justify-center font-mono text-xs border-r border-arch-border dark:border-dark-border ${layer.colorClass}`}>
                    0{layer.id}
                  </div>
                  <div className="p-4 flex-1 flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-arch-dark dark:text-white text-sm uppercase tracking-wide">{layer.name}</h4>
                      <p className="text-arch-gray dark:text-gray-400 text-xs mt-1">{layer.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Dimensions and Specs */}
          <motion.div className="space-y-8" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            
            <div className="border border-arch-border dark:border-dark-border bg-arch-light dark:bg-dark-surface p-8">
              <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-6 flex items-center uppercase tracking-widest">
                <SafeIcon icon={FiPackage} className="w-5 h-5 mr-4 text-brand-500" />
                Dostupné formáty
              </h3>
              <div className="space-y-4 font-mono text-sm">
                <div className="flex justify-between border-b border-arch-border dark:border-dark-border pb-3">
                  <span className="text-arch-gray dark:text-gray-400">Rozměr desky</span>
                  <span className="font-bold text-arch-dark dark:text-white">1100 × 2800 mm</span>
                </div>
                <div className="flex justify-between border-b border-arch-border dark:border-dark-border pb-3">
                  <span className="text-arch-gray dark:text-gray-400">Tloušťky</span>
                  <span className="font-bold text-arch-dark dark:text-white">5 / 8 / 12 mm</span>
                </div>
                <div className="flex justify-between pb-1">
                  <span className="text-arch-gray dark:text-gray-400">Minimální odběr</span>
                  <span className="font-bold text-arch-dark dark:text-white">50 ks</span>
                </div>
              </div>
            </div>

            <div className="border border-arch-border dark:border-dark-border bg-white dark:bg-dark-bg p-8">
              <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-6 flex items-center uppercase tracking-widest">
                <SafeIcon icon={FiTarget} className="w-5 h-5 mr-4 text-accent-sage" />
                Využití v praxi
              </h3>
              
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xs font-bold text-arch-dark dark:text-white uppercase tracking-widest mb-4 border-b border-arch-border dark:border-dark-border pb-2">Ideální pro</h4>
                  <ul className="space-y-2 text-sm text-arch-gray dark:text-gray-400">
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-teal mr-2"></span> Obklady stěn</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-teal mr-2"></span> Stropy</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-teal mr-2"></span> Nábytková čela</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-teal mr-2"></span> Interiérový design</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-arch-dark dark:text-white uppercase tracking-widest mb-4 border-b border-arch-border dark:border-dark-border pb-2">Nevhodné</h4>
                  <ul className="space-y-2 text-sm text-arch-gray dark:text-gray-400">
                    <li className="flex items-center"><span className="w-1.5 h-1.5 border border-accent-clay mr-2"></span> Podlahy</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 border border-accent-clay mr-2"></span> Sprchové kouty</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 border border-accent-clay mr-2"></span> Varné zóny</li>
                  </ul>
                </div>
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductInfo;